import {
  AdminHeader,
  AppShell,
  Box,
  getModuleUrl,
  LanguageSwitcher,
  Sidebar,
  useTenant,
  useTheme,
  useTokenVars,
  type SidebarItem,
  type ProfileMenuSection,
  i18n,
} from "@dehub/theme";
import { Spotlight, type SpotlightActionData } from "@mantine/spotlight";
import "@mantine/spotlight/styles.css";
import {
  BankIcon,
  ChartPieIcon,
  CoinsIcon,
  CreditCardIcon,
  FileTextIcon,
  GearIcon,
  HandshakeIcon,
  KeyIcon,
  LockKeyIcon,
  MagnifyingGlassIcon,
  MoneyWavyIcon,
  NetworkIcon,
  PackageIcon,
  PlugsConnectedIcon,
  BookOpenIcon,
  ReceiptIcon,
  ReceiptXIcon,
  UserCircleIcon,
  UserIcon,
  WarehouseIcon,
} from "@phosphor-icons/react";
import React, { JSX, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { notifTarget } from "../notifications/notif-target";
import useSWR from "swr";
import { LedgerApi, LogoutApi, NotificationApi } from "../../apis";
import { getModuleBadges } from "../../apis/network-badge";
import { ledgerGroupTitleKey } from "../../constants/ledger-sections";
import { useLedgerSections } from "../../hooks/useLedgerSections";
import { persistor } from "../../store";
import { IAuth } from "../../interfaces/IAuth";
import { message } from "../../utils/Message";
import { renderName } from "../../utils/render-name";
import { AppsMenuWithBadge } from "./AppsMenuWithBadge";
import { Breadcrumb } from "./breadcrumb";
import classes from "./Layout.module.css";
import { Page, PageHeader } from "./Page";

export { Page, PageHeader } from "./Page";

export function Layout() {
  useTokenVars();
  const [opened, setOpened] = useState<boolean>(false);
  const { tokens } = useTheme();
  const tenant = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("layout");
  const { user, accessToken, myBusinessType } = useSelector(
    (s: { auth: IAuth }) => s.auth,
  );

  const userName = renderName(user) || "";
  const userEmail = (user as { email?: string })?.email || "";
  const currentBusiness = (user as { currentBusiness?: { profileName?: string; partnerName?: string } })?.currentBusiness;
  const businessName = currentBusiness?.profileName || currentBusiness?.partnerName || "";
  const userInitial = (userName.trim().charAt(0) || "U").toUpperCase();

  // Auto-close mobile navbar on route change
  useEffect(() => {
    if (opened) setOpened(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const invoiceMenus = user?.permissions?.filter(
    (p: { module: string }) => p.module === "INVOICE",
  );

  const myModules: string[] = Array.from(
    new Set(
      (user?.permissions || [])
        .map((p: { module: string }) => p.module)
        .filter((mod): mod is string => !!mod),
    ),
  );

  // For BUYER users, hide /sales-invoice; for non-BUYER, hide /buyer-invoice
  const isHide = myBusinessType !== "BUYER" ? "/buyer-invoice" : "/sales-invoice";

  // Нээлттэй нэхэмжлэх sidebar badge = moduleBadges.INVOICE (same SENT count
  // that getModuleBadges already fetches). A separate open-invoice-sent-count
  // SWR duplicated that HTTP with a different key (no SWR dedupe).
  const openInvoiceLink = isHide === "/buyer-invoice" ? "/sales-invoice" : "/buyer-invoice";
  const isBuyer =
    (user as { currentBusiness?: { type?: string } })?.currentBusiness?.type ===
    "BUYER";
  const { data: moduleBadges } = useSWR(
    accessToken ? ["module-badges", isBuyer] : null,
    () => getModuleBadges(isBuyer),
    { revalidateOnFocus: false, dedupingInterval: 60000, shouldRetryOnError: false },
  );

  // SPRINT-05 Task 11 (Ruling 1) — gates the whole general-ledger nav group.
  // A business that never switched the internal ledger on must not see a
  // finance menu at all, and while the mode is still loading the group stays
  // hidden too: showing it to a business that must not have one is the worse
  // of the two flickers. No config row means OFF (§0.1).
  const { data: ledgerConfig, isLoading: ledgerConfigLoading } = useSWR(
    accessToken ? "ledger.config" : null,
    () => LedgerApi.getLedgerConfig(),
    { revalidateOnFocus: false, dedupingInterval: 60000, shouldRetryOnError: false },
  );
  const ledgerOn = !ledgerConfigLoading && ledgerConfig?.mode === "INTERNAL";

  type NavItem = {
    titleKey: string;
    icon: React.ComponentType<any>;
    link: string;
    menu: string;
    /** Нэмэлт цэс: `menu` эсвэл эдгээрийн аль нэгийг эзэмшсэн бол цэс идэвхтэй. */
    altMenus?: string[];
    supplierOnly: boolean;
    ledgerOnly: boolean;
  };

  const navConfig: NavItem[] = useMemo(
    () => [
      { titleKey: "nav.dashboard", icon: ChartPieIcon, link: "/", menu: "INVOICE_DASHBOARD", supplierOnly: false, ledgerOnly: false },
      { titleKey: "nav.openInvoice", icon: ReceiptIcon, link: "/sales-invoice", menu: "INVOICE_LIST", supplierOnly: false, ledgerOnly: false },
      { titleKey: "nav.openInvoice", icon: FileTextIcon, link: "/buyer-invoice", menu: "INVOICE_LIST", supplierOnly: false, ledgerOnly: false },
      { titleKey: "nav.closedInvoice", icon: ReceiptXIcon, link: "/closed-invoice", menu: "INVOICE_LIST_CLOSED", supplierOnly: false, ledgerOnly: false },
      { titleKey: "nav.customerBilling", icon: UserIcon, link: "/customer-billing", menu: "INVOICE_NETWORK_SETTLEMENT", supplierOnly: false, ledgerOnly: false },
      // «Тооцоо нийлэх» ба «Тооцооны хуудас» нэг цэс болсон: хоёр эрхийн аль нэг нь хүрэлцэнэ.
      { titleKey: "nav.settlement", icon: CoinsIcon, link: "/settlement", menu: "INVOICE_SETTLEMENT", altMenus: ["INVOICE_RECONCILIATION"], supplierOnly: false, ledgerOnly: false },
      { titleKey: "nav.returns", icon: ReceiptXIcon, link: "/refund-invoice", menu: "INVOICE_RETURNS", supplierOnly: false, ledgerOnly: false },
      // Дэвтрийн 16 хэсэг нэг цэс болсон: хэсгүүд src/constants/ledger-sections.ts-д,
      // /ledger hub дээр харагдана. `ledgerOnly: false` ЗААВАЛ — дэвтэр унтраалттай
      // үед ч Дэвтрийн тохиргоо (дэвтэр асаах цорын ганц хаалга) руу орох зам энэ.
      { titleKey: "nav.ledgerHub", icon: BookOpenIcon, link: "/ledger", menu: "INVOICE_SETTLEMENT", supplierOnly: false, ledgerOnly: false },
      { titleKey: "nav.settings", icon: GearIcon, link: "/settings", menu: "INVOICE_DASHBOARD", supplierOnly: true, ledgerOnly: false },
    ],
    [],
  );

  const tempHiddenLinks: string[] = [];

  const visibleNav = navConfig
    .filter((item) => !tempHiddenLinks.includes(item.link))
    .filter((item) => item.link !== isHide)
    .filter((item) => !item.supplierOnly || myBusinessType === "SUPPLIER")
    .filter((item) => !item.ledgerOnly || ledgerOn);

  const items: SidebarItem[] = visibleNav.map((n) => ({
    key: n.link,
    label: t(n.titleKey === "nav.customerBilling" && myBusinessType === "BUYER" ? "nav.customerBillingBuyer" : n.titleKey),
    link: n.link,
    icon: n.icon,
    disabled: !invoiceMenus?.some((p: { menu: string }) => p.menu === n.menu || n.altMenus?.includes(p.menu)),
    badgeCount: n.link === openInvoiceLink ? moduleBadges?.INVOICE : undefined,
  }));

  const activeItem = items.find(
    (it) =>
      location.pathname === it.link ||
      (it.link !== "/" && location.pathname.startsWith(it.link + "/")),
  );
  const activeKey = activeItem?.key;

  const { visible: ledgerVisible } = useLedgerSections();

  const pageTitle = t("titles.moduleTitle");

  const onLogout = async () => {
    try {
      await LogoutApi.logout();
    } catch (e) {
      const err = e as { message?: string };
      message.error(err?.message ?? "");
    } finally {
      // Persisted token-ийг устгаж, дараагийн зочлолтод хуучин token rehydrate
      // хийгдэхээс сэргийлнэ (reload-аас өмнө баталгаатай).
      await persistor.purge();
      window.location.href = `${getModuleUrl("", tenant?.modulesDomain)}/login?logout=1`;
    }
  };

  const onProfile = () => {
    window.location.href = `${getModuleUrl("", tenant?.modulesDomain)}/auth/profile/dan`;
  };

  const { data: notifData, mutate: refetchNotifs } = useSWR<{
    rows: NotifRow[];
    count: number;
  }>(
    "/2fa/auth/fcm/list",
    () => NotificationApi.list({ offset: { page: 1, limit: 20 } }),
    { fallbackData: { rows: [], count: 0 }, revalidateOnFocus: false },
  );
  const notifications = (notifData?.rows ?? []).map((n) => mapNotif(n));

  const onMarkAllNotifsRead = async () => {
    try {
      await Promise.all(
        (notifData?.rows ?? [])
          .filter((n) => !n.seen)
          .map((n) => NotificationApi.seen(n.id)),
      );
      refetchNotifs();
    } catch {
      // silent
    }
  };

  const onNotificationClick = async (n: { id: string | number; unread?: boolean; objectType?: string | null; objectId?: string | null }) => {
    try {
      if (n.unread) {
        await NotificationApi.seen(`${n.id}`);
        refetchNotifs();
      }
    } catch {
      // silent
    }
    const target = notifTarget(n.objectType, n.objectId, isBuyer);
    if (target) {
      if (target.module === "invoice") navigate(target.path);
      else window.location.href = `${m(target.module)}/access?token=${encodeURIComponent(accessToken ?? "")}&redirect=${encodeURIComponent(target.path)}`;
    }
  };

  const m = (mod: string) => getModuleUrl(mod, tenant?.modulesDomain);
  const tokenSuffix = `?token=${accessToken}`;
  const modulesAll = [
    { id: "network", label: t("moduleNetwork"), icon: NetworkIcon, href: `${m("network")}/access${tokenSuffix}`, active: false, enabled: myModules.includes("NETWORK") },
    { id: "invoice", label: t("moduleInvoice"), icon: ReceiptIcon, href: `${m("invoice")}/access${tokenSuffix}`, active: true, enabled: myModules.includes("INVOICE") },
    { id: "inventory", label: t("moduleInventory"), icon: WarehouseIcon, href: `${m("item")}/access${tokenSuffix}`, active: false, enabled: myModules.includes("INVENTORY") },
    { id: "payment", label: t("modulePayment"), icon: CreditCardIcon, href: `${m("payment")}/access${tokenSuffix}`, active: false, enabled: myModules.includes("PAYMENT") },
    { id: "order", label: t("moduleOrder"), icon: PackageIcon, href: `${m("order")}/access${tokenSuffix}`, active: false, enabled: myModules.includes("ORDER") },
    { id: "contract", label: t("moduleContract"), icon: FileTextIcon, href: `${m("contract")}/access${tokenSuffix}`, active: false, enabled: myModules.includes("CONTRACT") },
    { id: "price", label: t("modulePrice"), icon: CoinsIcon, href: `${m("price")}/access${tokenSuffix}`, active: false, enabled: myModules.includes("PRICING") },
    { id: "finance", label: t("moduleFinance"), icon: MoneyWavyIcon, href: `${m("")}?verifyConfirmType=modal`, active: false, enabled: myModules.includes("FINANCE") },
    { id: "consignment", label: t("moduleConsignment"), icon: HandshakeIcon, href: `${m("consignment")}/access${tokenSuffix}`, active: false, enabled: myModules.includes("CONSIGNMENT") },
    { id: "erp", label: t("moduleErp"), icon: PlugsConnectedIcon, href: `${m("erp")}/access${tokenSuffix}`, active: false, enabled: myModules.includes("ERP") },
  ];
  const BADGE_KEY: Record<string, string> = { network: "NETWORK", order: "ORDER", invoice: "INVOICE", contract: "CONTRACT", price: "PRICING" };
  const modules = modulesAll
    .filter((mod) => mod.enabled)
    .filter((mod) => !(isBuyer && (mod.id === "price" || mod.id === "consignment" || mod.id === "inventory")))
    .map(({ enabled: _enabled, ...rest }) => ({ ...rest, badge: moduleBadges?.[BADGE_KEY[rest.id] ?? ""] }));

  const navActions: SpotlightActionData[] = visibleNav
    .filter((n) =>
      invoiceMenus?.some((p: { menu: string }) => p.menu === n.menu),
    )
    .map((n) => ({
      id: n.link,
      label: t(n.titleKey),
      onClick: () => navigate(n.link),
      leftSection: <n.icon size={18} weight="duotone" />,
    }));

  // Дэвтрийн 16 хэсэг: нуух дүрэм /ledger hub-тай ижил (useLedgerSections),
  // эрхгүй (disabled) хэсгийг харуулахгүй.
  const ledgerActions: SpotlightActionData[] = ledgerVisible
    .filter((x) => !x.disabled)
    .map((x) => ({
      id: x.link,
      label: `${t("nav.ledgerHub")} › ${t(x.titleKey)}`,
      keywords: [t(ledgerGroupTitleKey(x.group)), t(x.descKey), t(x.titleKey)],
      onClick: () => navigate(x.link),
      leftSection: <x.icon size={18} weight="duotone" />,
    }));

  const profileSections: ProfileMenuSection[] = useMemo(() => {
    const authRoot = getModuleUrl("", tenant?.modulesDomain);
    return [
      {
        items: [
          { icon: UserCircleIcon, label: t("myProfile"), href: `${authRoot}/auth/profile/dan` },
          { icon: BankIcon, label: t("profilePartner"), href: `${getModuleUrl("partner", tenant?.modulesDomain)}/access?token=${accessToken ?? ""}` },
        ],
      },
      {
        items: [
          { icon: KeyIcon, label: t("profilePinChange"), href: `${authRoot}/auth/profile/dan?profileAction=pin` },
          { icon: LockKeyIcon, label: t("profilePasswordChange"), href: `${authRoot}/auth/profile/dan?profileAction=password` },
        ],
      },
    ];
  }, [tenant?.modulesDomain, accessToken, t]);

  return (
    <AppShell
      padding={0}
      header={{ height: 60 }}
      navbar={{
        // DeHUB UI стандарт: 104px. Tenant tokens.navbar.width нь стандартыг эвддэг тул ашиглахгүй.
        width: 104,
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      styles={{
        header: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 200 },
        // The page may never scroll sideways: wide tables scroll inside their own card.
        // Цагаан ажлын талбар (DeHUB UI стандарт): tenant-ийн саарал дэвсгэр картуудыг бүрхсэн мэт харагдуулдаг.
        main: { minWidth: 0, overflowX: "clip", backgroundColor: "var(--mantine-color-white)" },
      }}
    >
      <AppShell.Header>
        <AdminHeader
          tenant={{
            name: businessName,
            logo: tenant?.logo || null,
            domain: (tenant as { domain?: string })?.domain || "",
          }}
          pageTitle={pageTitle}
          headerGradient={tokens.header.gradient}
          headerTextColor={tokens.header.textColor}
          userName={userName}
          userEmail={userEmail}
          userInitial={userInitial}
          userAvatar={(user as any)?.avatar || undefined}
          onProfile={onProfile}
          onLogout={onLogout}
          onSwitchTenant={() => {
            window.location.href = `${getModuleUrl("", tenant?.modulesDomain)}/businesses`;
          }}
          notifications={notifications}
          onMarkAllNotifsRead={onMarkAllNotifsRead}
          onNotificationClick={onNotificationClick}
          preNotificationsSlot={<AppsMenuWithBadge modules={modules} align="right" />}
          localeSlot={
            <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <LanguageSwitcher />
            </Box>
          }
          logoHref={m("cp")}
          profileSections={profileSections}
        />
      </AppShell.Header>

      <AppShell.Navbar p={0} style={{ background: "transparent", border: "none" }}>
        <Sidebar
          items={items}
          activeKey={activeKey}
          onPick={(_key, link) => {
            navigate(link);
            setOpened(false);
          }}
          headerStyle="dark"
        />
      </AppShell.Navbar>

      <AppShell.Main className={classes.main}>
        <Outlet />
      </AppShell.Main>

      <Spotlight
        actions={[...navActions, ...ledgerActions]}
        nothingFound={t("searchNotFound")}
        highlightQuery
        shortcut={["mod + K", "mod + P", "/"]}
        searchProps={{
          leftSection: <MagnifyingGlassIcon size={18} weight="duotone" />,
          placeholder: t("searchPlaceholder"),
        }}
        radius="md"
      />
    </AppShell>
  );
}

type PageProps = {
  title: string | JSX.Element;
  subTitle?: string | JSX.Element;
  extra?: React.ReactNode[];
  children: React.ReactNode;
  breadcrumb?: { to?: string; label: string }[];
  bgColor?: string;
  loading?: boolean;
};

export function PageLayout({ title, subTitle, breadcrumb, bgColor: _bgColor, extra = [], children }: PageProps) {
  return (
    <Page>
      <PageHeader
        breadcrumb={breadcrumb ? <Breadcrumb data={breadcrumb} /> : undefined}
        title={title}
        subtitle={subTitle}
        extra={extra.length > 0 ? <>{extra}</> : undefined}
      />
      {children}
    </Page>
  );
}

type FormProps = {
  title: string | JSX.Element;
  subTitle?: string | JSX.Element;
  extra?: React.ReactNode[];
  children: React.ReactNode;
  bgColor?: string;
  my?: number | string;
};

export function FormLayout({ title, subTitle, bgColor: _bgColor, extra = [], children, my = 0 }: FormProps) {
  return (
    <Page>
      <Box my={my || undefined}>
        <PageHeader
          title={title}
          subtitle={subTitle}
          extra={extra.length > 0 ? <>{extra}</> : undefined}
        />
        {children}
      </Box>
    </Page>
  );
}

type FilterProps = {
  left?: JSX.Element[];
  right?: JSX.Element[];
};

export function PageFilter({ left, right }: FilterProps) {
  return (
    <Box className={classes.pageFilter}>
      <Box className={classes.sectionElement}>{left}</Box>
      <Box className={classes.sectionElement}>{right}</Box>
    </Box>
  );
}

// ───── Notification adapter ─────

type NotifRow = {
  id: string;
  title?: string;
  subject?: string;
  body?: string;
  message?: string;
  type?: string;
  category?: string;
  createdAt?: string;
  seen?: boolean;
  actionLabel?: string;
  objectType?: string | null;
  objectId?: string | null;
};

function mapNotif(n: NotifRow) {
  const created = n.createdAt ? new Date(n.createdAt) : new Date();
  const ageDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
  const group: "today" | "week" = ageDays < 1 ? "today" : "week";
  return {
    id: n.id,
    kind: detectKind(n.type ?? n.category ?? n.body),
    title: n.title ?? n.subject ?? i18n.t("layout:notifications"),
    body: n.body ?? n.message ?? "",
    time: relativeTime(created),
    ts: created.toISOString().slice(0, 16).replace("T", " "),
    group,
    unread: !n.seen,
    action: n.actionLabel,
    objectType: n.objectType ?? null,
    objectId: n.objectId ?? null,
  };
}

function detectKind(value?: string) {
  const v = String(value || "").toLowerCase();
  if (v.includes("order")) return "order" as const;
  if (v.includes("payment") || v.includes("төлбөр")) return "payment" as const;
  if (v.includes("contract") || v.includes("гэрээ")) return "contract" as const;
  if (v.includes("partner") || v.includes("партнер")) return "partner" as const;
  return "alert" as const;
}

function relativeTime(d: Date): string {
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return i18n.t("layout:relativeTime.now");
  const min = Math.floor(sec / 60);
  if (min < 60) return i18n.t("layout:relativeTime.minutesAgo", { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return i18n.t("layout:relativeTime.hoursAgo", { count: hr });
  const day = Math.floor(hr / 24);
  if (day === 1) return i18n.t("layout:relativeTime.yesterday");
  if (day < 7) return i18n.t("layout:relativeTime.daysAgo", { count: day });
  return d.toLocaleDateString(i18n.language);
}
